import type { tPrompt } from '../../types';
import type { ServiceProcedure, ServiceProcedureConsumable } from '../../types/companyResourceTypes';
import type { PMPartsAndProcedure } from '../../types/pmTypes';
import { FORM_ITEM_CATEGORIES, OPERATOR_RESPONSE_TYPES } from '../../utils/constants';
import {
    linkedConsumablesForProcedure,
    procedureConsumableRateLabel,
} from '../../utils/companyResourceUtils';
import {
    DS_SECTION_CATALOG,
    isDsSectionCatalogKind,
    type DsSectionCatalogKind,
} from './formBuilderServiceCatalog';

export function isDailyServiceProcedurePrompt(
    prompt?: Pick<tPrompt, 'serviceProcedureId' | 'operatorResponseType' | 'category'> | null,
): boolean {
    if (!prompt) return false;
    return (
        Boolean(prompt.serviceProcedureId) ||
        prompt.operatorResponseType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT ||
        prompt.category === FORM_ITEM_CATEGORIES.PROCEDURE
    );
}

export function resolveSectionCatalogKind(section?: {
    catalogKind?: string | null;
    prompts?: Array<Pick<tPrompt, 'serviceProcedureId' | 'operatorResponseType' | 'category' | 'component'>> | null;
} | null): DsSectionCatalogKind | null {
    if (isDsSectionCatalogKind(section?.catalogKind)) return section.catalogKind;
    const prompts = section?.prompts || [];
    if (prompts.length === 0) return null;
    if (prompts.every((prompt) => isDailyServiceProcedurePrompt(prompt))) {
        return DS_SECTION_CATALOG.PROCEDURES;
    }
    return DS_SECTION_CATALOG.SERVICE_ITEMS;
}

/** Daily Service section headings stay on catalog SearchSuggest after save/reload. */
export function sectionUsesCatalogSuggest(
    isDailyService: boolean,
    section?: {
        catalogKind?: string | null;
        clientId?: string;
        prompts?: Array<Pick<tPrompt, 'serviceProcedureId' | 'operatorResponseType' | 'category' | 'component'>> | null;
    } | null,
    options?: { isNewForm?: boolean },
): boolean {
    if (!isDailyService) return false;
    if (options?.isNewForm || section?.clientId) return true;
    return resolveSectionCatalogKind(section) != null;
}

export function withResolvedCatalogKind<T extends {
    catalogKind?: string | null;
    prompts?: Array<Pick<tPrompt, 'serviceProcedureId' | 'operatorResponseType' | 'category' | 'component'>> | null;
}>(section: T): T & { catalogKind?: string | null } {
    if (isDsSectionCatalogKind(section.catalogKind)) return section;
    const kind = resolveSectionCatalogKind(section);
    if (!kind) return section;
    return { ...section, catalogKind: kind };
}

export function createDailyServiceProcedurePrompt(): tPrompt {
    return {
        clientId: crypto.randomUUID(),
        component: '',
        description: '',
        category: FORM_ITEM_CATEGORIES.PROCEDURE,
        operatorResponseType: OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT,
        autoFlag: false,
    };
}

/** Defaults when adding or recategorizing a Daily Service consumable row. */
export function promptPatchForDailyServiceCategory(category?: string | null): Partial<tPrompt> {
    if (category === FORM_ITEM_CATEGORIES.IMPLEMENT_SERVICE) {
        return {
            operatorResponseType: OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY,
            autoFlag: true,
        };
    }
    if (category === FORM_ITEM_CATEGORIES.NORMAL_CONSUMABLE) {
        return {
            operatorResponseType: OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY,
            autoFlag: false,
        };
    }
    return {};
}

export function procedureSuggestLabel(
    procedure: Pick<ServiceProcedure, 'itemName' | 'category'>,
): string {
    return `${procedure.itemName.trim()} (${procedure.category.trim()})`;
}

/** True when the SearchSuggest input still shows the already-bound procedure. */
export function isBoundProcedureSuggestText(
    text: string,
    bound?: Pick<ServiceProcedure, 'id' | 'itemName' | 'category'> | null,
    component?: string | null,
): boolean {
    const trimmed = text.trim().toLowerCase();
    if (!trimmed) return false;
    if (bound) {
        if (bound.id.toLowerCase() === trimmed) return true;
        if (bound.itemName.trim().toLowerCase() === trimmed) return true;
        if (procedureSuggestLabel(bound).toLowerCase() === trimmed) return true;
    }
    return Boolean(component?.trim() && component.trim().toLowerCase() === trimmed);
}

export function findProcedureForSuggestInput(
    procedures: ServiceProcedure[],
    text: string,
): ServiceProcedure | undefined {
    const trimmed = text.trim();
    if (!trimmed) return undefined;
    const byId = procedures.find((procedure) => procedure.id === trimmed);
    if (byId) return byId;

    const byLabel = procedures.filter(
        (procedure) => procedureSuggestLabel(procedure).toLowerCase() === trimmed.toLowerCase(),
    );
    if (byLabel.length === 1) return byLabel[0];

    const byName = procedures.filter(
        (procedure) => procedure.itemName.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (byName.length === 1) return byName[0];
    return undefined;
}

export function sortedProcedureSuggestOptions(
    procedures: ServiceProcedure[],
    preferCategory?: string,
): { value: string; label: string }[] {
    const prefer = preferCategory?.trim().toLowerCase() || '';
    return [...procedures]
        .filter((procedure) => procedure.itemName?.trim())
        .sort((a, b) => {
            if (prefer) {
                const aMatch = a.category.trim().toLowerCase() === prefer ? 0 : 1;
                const bMatch = b.category.trim().toLowerCase() === prefer ? 0 : 1;
                if (aMatch !== bMatch) return aMatch - bMatch;
            }
            return (
                a.itemName.localeCompare(b.itemName, undefined, { sensitivity: 'base' }) ||
                a.category.localeCompare(b.category, undefined, { sensitivity: 'base' })
            );
        })
        .map((procedure) => ({
            value: procedure.id,
            label: procedureSuggestLabel(procedure),
        }));
}

export function procedureConsumablesHelperText(
    procedureId: string,
    links: ServiceProcedureConsumable[],
    catalog: PMPartsAndProcedure[],
): string {
    const rows = linkedConsumablesForProcedure(links, catalog, procedureId);
    if (rows.length === 0) return '';
    return rows
        .map(({ link, item }) => {
            const name = item?.itemName?.trim() || 'Unavailable item';
            const rate = procedureConsumableRateLabel(link.quantity, item?.unitOfMeasure);
            return rate === '—' ? name : `${name} · ${rate}`;
        })
        .join('; ');
}

export function applyProcedureToPrompt(
    procedure: ServiceProcedure,
    links: ServiceProcedureConsumable[],
    catalog: PMPartsAndProcedure[],
): Partial<tPrompt> {
    const helper = procedureConsumablesHelperText(procedure.id, links, catalog);
    return {
        component: procedure.itemName.trim(),
        serviceProcedureId: procedure.id,
        category: FORM_ITEM_CATEGORIES.PROCEDURE,
        operatorResponseType: OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT,
        autoFlag: false,
        serviceItemId: undefined,
        ...(helper ? { description: helper } : {}),
    };
}
